<template>
  <div id="owner-financial-statement">
    
    <!-- 1. كارت تفاصيل ومعلومات المالك (Owner Profile Header) -->
    <vx-card class="mb-6 shadow-sm" v-if="financialData.ownerId">
      <div class="flex flex-wrap items-center justify-between">
        <div class="flex items-center mb-2 md:mb-0">
          <div class="p-3 bg-primary-light rounded-full mr-4 rtl:ml-4 rtl:mr-0 text-primary">
            <feather-icon icon="UserIcon" svgClasses="w-8 h-8" />
          </div>
          <div>
            <h4 class="font-bold mb-1">
              {{ currentLocale === 'ar' ? (financialData.fullNameAr || financialData.fullNameEn) : (financialData.fullNameEn || financialData.fullNameAr) }}
            </h4>
            <div class="flex flex-wrap items-center text-sm text-muted gap-3">
              <span v-if="financialData.phoneNumber" class="flex items-center mr-3 rtl:ml-3">
                <feather-icon icon="PhoneIcon" svgClasses="w-4 h-4 mr-1 rtl:ml-1 text-primary" />
                {{ financialData.phoneNumber }}
              </span>
              <span v-if="financialData.email" class="flex items-center mr-3 rtl:ml-3">
                <feather-icon icon="MailIcon" svgClasses="w-4 h-4 mr-1 rtl:ml-1 text-primary" />
                {{ financialData.email }}
              </span>
              <span v-if="financialData.erpAccountId" class="flex items-center">
                <feather-icon icon="HashIcon" svgClasses="w-4 h-4 mr-1 rtl:ml-1 text-primary" />
                ERP ID: <strong>{{ financialData.erpAccountId }}</strong>
              </span>
            </div>
          </div>
        </div>

        <!-- حالة المالك ورقم المالك -->
        <div class="flex items-center gap-2">
          <vs-chip :color="financialData.isActive ? 'success' : 'danger'">
            {{ financialData.isActive ? $t('Active') : $t('Inactive') }}
          </vs-chip>
          <vs-button icon-pack="feather" icon="icon-printer" type="border" size="small" @click="printStatement">
            {{ $t('Print') }}
          </vs-button>
        </div>
      </div>
    </vx-card>

    <!-- 2. كروت الإحصائيات المالية والفرص (Summary Cards) -->
<!-- 2. كروت الإحصائيات المالية والفرص -->
<div class="vx-row mb-6" v-if="financialData.ownerId">

  <!-- إجمالي الاستحقاقات -->
  <div class="vx-col w-full sm:w-1/2 lg:w-1/3 xl:w-1/5 mb-base">
    <vx-card class="bg-primary text-white">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium opacity-75 mb-1">
            {{ $t('TotalEntitlements') }}
          </p>

          <h3 class="text-white font-bold">
            {{ formatCurrency(financialData.totalEntitlements) }}
          </h3>
        </div>

        <div class="p-3 bg-white bg-opacity-25 rounded-full">
          <feather-icon
            icon="TrendingUpIcon"
            svgClasses="w-8 h-8 text-white"
          />
        </div>
      </div>
    </vx-card>
  </div>

  <!-- إجمالي عمولات المالك -->
  <div class="vx-col w-full sm:w-1/2 lg:w-1/3 xl:w-1/5 mb-base">
    <vx-card class="bg-danger text-white">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium opacity-75 mb-1">
            {{ $t('TotalCommissions') }}
          </p>

          <h3 class="text-white font-bold">
            {{ formatCurrency(financialData.totalCommissions) }}
          </h3>
        </div>

        <div class="p-3 bg-white bg-opacity-25 rounded-full">
          <feather-icon
            icon="PercentIcon"
            svgClasses="w-8 h-8 text-white"
          />
        </div>
      </div>
    </vx-card>
  </div>

  <!-- إجمالي المدفوعات -->
  <div class="vx-col w-full sm:w-1/2 lg:w-1/3 xl:w-1/5 mb-base">
    <vx-card class="bg-success text-white">
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium opacity-75 mb-1">
            {{ $t('TotalPaid') }}
          </p>

          <h3 class="text-white font-bold">
            {{ formatCurrency(financialData.totalPaid) }}
          </h3>
        </div>

        <div class="p-3 bg-white bg-opacity-25 rounded-full">
          <feather-icon
            icon="CheckSquareIcon"
            svgClasses="w-8 h-8 text-white"
          />
        </div>
      </div>
    </vx-card>
  </div>

  <!-- الرصيد الحالي -->
  <div class="vx-col w-full sm:w-1/2 lg:w-1/3 xl:w-1/5 mb-base">
    <vx-card
      :class="financialData.currentBalance > 0
        ? 'bg-warning'
        : 'bg-dark'"
      class="text-white"
    >
      <div class="flex items-center justify-between">
        <div>
          <p class="font-medium opacity-75 mb-1">
            {{ $t('CurrentBalance') }}
          </p>

          <h3 class="text-white font-bold">
            {{ formatCurrency(financialData.currentBalance) }}
          </h3>
        </div>

        <div class="p-3 bg-white bg-opacity-25 rounded-full">
          <feather-icon
            icon="DollarSignIcon"
            svgClasses="w-8 h-8 text-white"
          />
        </div>
      </div>
    </vx-card>
  </div>

<!-- عدد الفرص -->
<div class="vx-col w-full sm:w-1/2 lg:w-1/3 xl:w-1/5 mb-base">
  <vx-card class="opportunities-card text-white">
    <div class="flex items-center justify-between">
      <div>
        <p class="font-medium opacity-75 mb-1 text-white">
          {{ $t('OpportunitiesCount') }}
        </p>

        <h3 class="text-white font-bold">
          {{ financialData.opportunitiesCount || 0 }}
        </h3>
      </div>

      <div class="p-3 bg-white bg-opacity-25 rounded-full">
        <feather-icon
          icon="HomeIcon"
          svgClasses="w-8 h-8 text-white"
        />
      </div>
    </div>
  </vx-card>
</div>

</div>

    <!-- 3. جدول الحركات المالية (Transactions Table) -->
    <vx-card :title="$t('FinancialTransactionsList')">
      <template slot="actions">
        <vs-button icon-pack="feather" icon="icon-refresh-cw" size="small" type="border" @click="loadData"></vs-button>
      </template>

      <vs-table pagination :max-items="itemsPerPage" search :data="financialData.transactions || []">
        <template slot="thead">
          <vs-th sort-key="transactionDate">{{ $t('TransactionDate') }}</vs-th>
          <vs-th sort-key="transactionTypeId">{{ $t('Type') }}</vs-th>
          <vs-th sort-key="amount">{{ $t('Amount') }}</vs-th>
          <vs-th sort-key="referenceNumber">{{ $t('ReferenceNumber') }}</vs-th>
          <vs-th sort-key="opportunityId">{{ $t('OpportunityNo') }}</vs-th>
          <vs-th>{{ $t('Notes') }}</vs-th>
        </template>

        <template slot-scope="{data}">
          <tbody>
            <tr v-for="(tr, index) in data" :key="index">
              <!-- تاريخ الحركة -->
              <vs-td>{{ formatDate(tr.transactionDate) }}</vs-td>

              <!-- نوع الحركة -->
              <vs-td>
                <vs-chip :color="getTransactionColor(tr.transactionTypeId)">
                  {{ getTransactionTypeText(tr.transactionTypeId) }}
                </vs-chip>
              </vs-td>

              <!-- المبلغ -->
              <vs-td class="font-bold">
                <span :class="getTransactionTextClass(tr.transactionTypeId)">
                  {{ getTransactionSign(tr.transactionTypeId) }} {{ formatCurrency(tr.amount) }}
                </span>
              </vs-td>

              <!-- رقم المرجع -->
              <vs-td>{{ tr.referenceNumber || '-' }}</vs-td>

              <!-- رقم الفرصة -->
              <vs-td>{{ tr.opportunityId || '-' }}</vs-td>

              <!-- ملاحظات -->
              <vs-td>{{ tr.notes || '-' }}</vs-td>
            </tr>
          </tbody>
        </template>
      </vs-table>
    </vx-card>
  </div>
</template>

<script>
import VxCard from "../../components/vx-card/VxCard.vue";

export default {
  name: "PropertyOwnerFinancialStatement",
  components: { VxCard },
  props: {
    ownerId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      itemsPerPage: 10,
     financialData: {
          ownerId: 0,
          ownerCode: "",
          fullNameAr: "",
          fullNameEn: "",
          phoneNumber: "",
          email: "",
          erpAccountId: null,
          isActive: true,
          opportunitiesCount: 0,
          totalEntitlements: 0,
          totalCommissions: 0,
          totalPaid: 0,
          currentBalance: 0,
          transactions: []
        }
    };
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale;
    }
  },
methods: {
  normalizeTransactions(transactions) {
    if (!transactions || !Array.isArray(transactions)) {
      return [];
    }

    return transactions.map(function (transaction) {
      return {
        id:
          transaction.Id !== undefined
            ? transaction.Id
            : transaction.id,

        opportunityId:
          transaction.OpportunityId !== undefined
            ? transaction.OpportunityId
            : transaction.opportunityId,

        amount:
          transaction.Amount !== undefined
            ? transaction.Amount
            : transaction.amount,

        transactionTypeId:
          transaction.TransactionTypeId !== undefined
            ? transaction.TransactionTypeId
            : transaction.transactionTypeId,

        referenceNumber:
          transaction.ReferenceNumber !== undefined
            ? transaction.ReferenceNumber
            : transaction.referenceNumber,

        notes:
          transaction.Notes !== undefined
            ? transaction.Notes
            : transaction.notes,

        transactionDate:
          transaction.TransactionDate !== undefined
            ? transaction.TransactionDate
            : transaction.transactionDate,

            opportunitiesCount:
            transaction.OpportunitiesCount !== undefined
              ? transaction.OpportunitiesCount
              : transaction.opportunitiesCount || 0,
      };
    });
  },

  getTransactionTypeText(typeId) {
    switch (Number(typeId)) {
      case 1:
        return this.$t("Entitlement");

      case 2:
        return this.$t("Payment");

      case 3:
        return this.$t("OwnerCommission");

      default:
        return this.$t("Unknown");
    }
  },

  getTransactionColor(typeId) {
    switch (Number(typeId)) {
      case 1:
        return "primary";

      case 2:
        return "success";

      case 3:
        return "danger";

      default:
        return "dark";
    }
  },

  getTransactionSign(typeId) {
    return Number(typeId) === 1
      ? "+"
      : "-";
  },

  getTransactionTextClass(typeId) {
    switch (Number(typeId)) {
      case 1:
        return "text-primary";

      case 2:
        return "text-success";

      case 3:
        return "text-danger";

      default:
        return "";
    }
  },

  loadData() {
    var currentOwnerId =
      Number(this.ownerId);

    if (
      !currentOwnerId ||
      currentOwnerId <= 0
    ) {
      return;
    }

    this.$vs.loading();

    this.$store
      .dispatch(
        "PropertyOwnersList/fetchOwnerFinancialStatement",
        currentOwnerId
      )
      .then((response) => {
        var result = null;

        if (
          response &&
          response.Data
        ) {
          result = response.Data;
        } else if (
          response &&
          response.data
        ) {
          result = response.data;
        } else {
          result = response;
        }

        if (!result) {
          throw new Error(
            "Financial statement response has no data."
          );
        }

        this.financialData = {
          ownerId:
            result.OwnerId !== undefined
              ? result.OwnerId
              : result.ownerId,

          ownerCode:
            result.OwnerCode !== undefined
              ? result.OwnerCode
              : result.ownerCode,

          fullNameAr:
            result.FullNameAr !== undefined
              ? result.FullNameAr
              : result.fullNameAr,

          fullNameEn:
            result.FullNameEn !== undefined
              ? result.FullNameEn
              : result.fullNameEn,

          phoneNumber:
            result.PhoneNumber !== undefined
              ? result.PhoneNumber
              : result.phoneNumber,

          email:
            result.Email !== undefined
              ? result.Email
              : result.email,

          erpAccountId:
            result.ErpAccountId !== undefined
              ? result.ErpAccountId
              : result.erpAccountId,

          isActive:
            result.IsActive !== undefined
              ? result.IsActive
              : result.isActive,

          opportunitiesCount:
            result.OpportunitiesCount !== undefined
              ? result.OpportunitiesCount
              : result.opportunitiesCount || 0,

          totalEntitlements:
            result.TotalEntitlements !== undefined
              ? result.TotalEntitlements
              : result.totalEntitlements || 0,

          totalCommissions:
            result.TotalCommissions !== undefined
              ? result.TotalCommissions
              : result.totalCommissions || 0,

          totalPaid:
            result.TotalPaid !== undefined
              ? result.TotalPaid
              : result.totalPaid || 0,

          currentBalance:
            result.CurrentBalance !== undefined
              ? result.CurrentBalance
              : result.currentBalance || 0,

          transactions:
            this.normalizeTransactions(
              result.Transactions ||
              result.transactions ||
              []
            )
        };
      })
      .catch((err) => {
        console.error(err);

        var errorMessage =
          this.$t("ErrorOccurred");

        if (
          err &&
          err.response &&
          err.response.data
        ) {
          errorMessage =
            err.response.data.Message ||
            err.response.data.message ||
            errorMessage;
        }

        this.$vs.notify({
          title: this.$t("Error"),
          text: errorMessage,
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "danger"
        });
      })
      .then(() => {
        this.$vs.loading.close();
      });
  },

  formatCurrency(val) {
    if (
      val === null ||
      val === undefined
    ) {
      return "0.00";
    }

    return Number(val).toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );
  },

  formatDate(dateStr) {
    if (!dateStr) {
      return "-";
    }

    return new Date(
      dateStr
    ).toLocaleDateString();
  },

  printStatement() {
    window.print();
  }
},
  created() {
  this.loadData();
},

watch: {
  ownerId: {
    handler(newValue, oldValue) {
      if (
        Number(newValue) > 0 &&
        Number(newValue) !== Number(oldValue)
      ) {
        this.loadData();
      }
    },

    immediate: false
  }
}
};
</script>
<style scoped>
.opportunities-card {
  background-color: #2196f3 !important;
  color: #ffffff !important;
}

.opportunities-card h3,
.opportunities-card p {
  color: #ffffff !important;
}
</style>